import {useState, useEffect} from 'react';
import handleError from '../../../../../lib/error';
import {networkOptimization} from '../../../../../lib/api';
import type NetworkOptimizationStore from '../../../lib/store';

interface UseProviderPaginationParams {
    networkOptimizationStore: NetworkOptimizationStore;
}

interface UseProviderPaginationReturn {
    loading: boolean;
    error: string | null;
    currentPage: number;
    totalPages: number;
    totalCount: number | null;
    loadingMore: boolean;
    loadMoreProviders: () => Promise<void>;
    getLoadMoreButtonText: () => string;
    getRecordCountText: () => string;
}

export function useProviderPagination({
    networkOptimizationStore
}: UseProviderPaginationParams): UseProviderPaginationReturn {
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [totalPages, setTotalPages] = useState<number>(1);
    const [totalCount, setTotalCount] = useState<number | null>(null);
    const [loadingMore, setLoadingMore] = useState<boolean>(false);

    // Initial data load when filters change
    useEffect(() => {
        const controller = new AbortController();
        if (!networkOptimizationStore.specialtyFilter) {
            networkOptimizationStore.resetProviders();
            setLoading(false);
            setError(null);
            return;
        }
        setLoading(true);
        setError(null);
        // Reset pagination state when filters change
        setCurrentPage(1);
        setTotalPages(1);
        setTotalCount(null);

        const loadData = async () => {
            try {
                const paginationInfo = await networkOptimizationStore.loadLocalProviders(controller);
                // Update pagination state if response is paginated
                if (paginationInfo) {
                    setCurrentPage(paginationInfo.current_page);
                    setTotalPages(paginationInfo.total_pages);
                    setTotalCount(paginationInfo.total_count);
                }
                networkOptimizationStore.deselectAllAdditionalProviders();
                networkOptimizationStore.selectAllExistingProviders();
                setLoading(false);
            } catch (err) {
                handleError(err as Error, setError);
            }
        };

        loadData();

        return () => controller.abort();
    }, [
        networkOptimizationStore.specialtyFilter,
        networkOptimizationStore.appliedGeographyFilter,
        networkOptimizationStore.runId,
        networkOptimizationStore.revisionId,
        networkOptimizationStore.distanceCalcType,
        networkOptimizationStore.rosterRunCount
    ]);

    // Load more providers for pagination
    const loadMoreProviders = async () => {
        if (loadingMore || currentPage >= totalPages) return;

        setLoadingMore(true);
        setError(null);

        try {
            const controller = new AbortController();
            const nextPage = currentPage + 1;

            let filters: Record<string, string> = {
                ...networkOptimizationStore.geographyFilters
            };
            if (networkOptimizationStore.specialtyFilter) {
                filters.specialty = networkOptimizationStore.specialtyFilterCode;
            }

            // Fetch next page
            const response = await networkOptimization.fetchProviders(
                filters,
                networkOptimizationStore.runId!,
                networkOptimizationStore.revisionId ?? 0,
                networkOptimizationStore.distanceCalcType,
                controller.signal,
                nextPage
            );

            // Handle paginated response
            const newProviders = Array.isArray(response) ? response : response.data || [];
            const mappedProviders = newProviders.map((provider: any) => ({
                ...provider,
                id: provider.provider_address_fact_key
            }));

            // Append to store's local providers
            networkOptimizationStore.appendLocalProviders(mappedProviders);

            // Update pagination state
            if (!Array.isArray(response)) {
                setCurrentPage(response.current_page);
            } else {
                setCurrentPage(nextPage);
            }
        } catch (err) {
            handleError(err as Error, setError);
        } finally {
            setLoadingMore(false);
        }
    };

    // Helper function to generate button text
    const getLoadMoreButtonText = () => {
        if (loadingMore) {
            return 'Loading...';
        }

        if (currentPage >= totalPages) {
            return 'All Providers Loaded';
        }

        return 'Load More Records';
    };

    // Helper function to generate record count text
    const getRecordCountText = () => {
        const loadedCount = networkOptimizationStore.localProviders.length;

        if (totalCount) {
            return `${loadedCount.toLocaleString()} of ${totalCount.toLocaleString()} loaded`;
        }

        return `${loadedCount.toLocaleString()}+ loaded`;
    };

    return {
        loading,
        error,
        currentPage,
        totalPages,
        totalCount,
        loadingMore,
        loadMoreProviders,
        getLoadMoreButtonText,
        getRecordCountText
    };
}
